import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import FilmCard from '@/components/base/FilmCard';
import { filmsData, categoriesData } from '@/mocks/films';

const categoryDescriptions: Record<string, string> = {
  featured: '编辑团队精选的北条麻妃必看作品，适合初次接触的影迷入门欣赏。',
  classic: '北条麻妃演艺生涯中的经典代表作，经久不衰、历久弥新的传世佳作。',
  latest: '北条麻妃最新上映的作品合集，紧跟动态，第一时间掌握新作信息。',
  'top-rated': '影迷评分最高的北条麻妃作品，口碑与品质的双重保证。',
  exclusive: '限量发行与特别版北条麻妃作品，影迷不可错过的珍藏佳品。',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'featured';
  const category = categoriesData.find((c) => c.slug === currentSlug) || categoriesData[0];
  const [filteredFilms, setFilteredFilms] = useState(filmsData.filter((f) => f.category === currentSlug));

  useEffect(() => {
    setFilteredFilms(filmsData.filter((f) => f.category === currentSlug));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentSlug]);

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hojomaki-film.com/' },
              { '@type': 'ListItem', position: 2, name: category.name, item: `https://hojomaki-film.com/category/${currentSlug}` },
            ],
          }),
        }}
      />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <div className="bg-background-950">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />
              <div className="flex items-center gap-4 mt-2">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-400">
                  <i className={`${category.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-sm text-background-400">
                    {categoryDescriptions[currentSlug] || category.description}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-1 py-3 overflow-x-auto flex-wrap">
                {categoriesData.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                      currentSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <section className="py-10 md:py-14" data-product-shop>
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              {filteredFilms.length > 0 ? (
                <>
                  <div className="flex items-center justify-between mb-8">
                    <p className="text-sm text-foreground-600">
                      共找到 <strong className="text-foreground-900">{filteredFilms.length}</strong> 部作品
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">排序:</span>
                      <select className="text-sm bg-background-50 border border-background-200/70 rounded-lg px-3 py-1.5 text-foreground-700 cursor-pointer outline-none focus:border-primary-300">
                        <option value="latest">最新发布</option>
                        <option value="rating">评分最高</option>
                        <option value="title">名称排序</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {filteredFilms.map((film) => (
                      <FilmCard key={film.id} {...film} />
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                    <i className="ri-film-line text-2xl"></i>
                  </div>
                  <p className="mt-4 text-foreground-600">该分类下暂无作品</p>
                  <Link
                    to="/category/featured"
                    className="inline-flex items-center gap-1.5 mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    返回精选推荐
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}